/**
 * Structured outfit description. Both the roster side (check-in photo, email
 * answer, staff note) and the camera side (Haiku on crossing crops) are
 * normalised into this shape so matching is deterministic.
 */
export interface Outfit {
  /** e.g. "singlet", "t-shirt", "long sleeve", "jacket", "crop top". */
  top?: string | null;
  topColor?: string | null;
  /** e.g. "shorts", "tights", "skirt". */
  bottom?: string | null;
  bottomColor?: string | null;
  /** e.g. "cap", "visor", "headband", "none". */
  headwear?: string | null;
  headwearColor?: string | null;
  /** Main shoe colour. */
  shoesColor?: string | null;
  /** e.g. "ponytail", "bun", "braids", "short", "long loose", "shaved". */
  hair?: string | null;
  hairColor?: string | null;
  /** Prints, stripes, logos or text on the clothing, e.g. "white side stripes", "text BERLIN RC". */
  marks?: string | null;
  /** Accessories: sunglasses, hydration vest, gloves, arm sleeves, compression socks, watch… */
  other?: string[];
}

/**
 * A camera sees the same things on a runner at every point, so attributes
 * read from earlier crossings are much stronger evidence than the roster's
 * free-text description, which rarely mentions more than a top.
 */

/** Colour families: an exact family match scores 1, a related family 0.5. */
const COLOR_FAMILY: Record<string, string> = {
  red: 'red', maroon: 'red', burgundy: 'red', crimson: 'red',
  orange: 'orange', coral: 'orange',
  yellow: 'yellow', gold: 'yellow',
  green: 'green', lime: 'green', olive: 'green',
  teal: 'teal', turquoise: 'teal', aqua: 'teal',
  blue: 'blue', navy: 'blue', 'light blue': 'blue', royal: 'blue', cyan: 'teal',
  purple: 'purple', violet: 'purple', lavender: 'purple',
  pink: 'pink', magenta: 'pink', fuchsia: 'pink',
  white: 'white', cream: 'white',
  grey: 'grey', gray: 'grey', silver: 'grey',
  black: 'black', charcoal: 'black',
  brown: 'brown', tan: 'brown', beige: 'brown',
  // Hair
  blonde: 'yellow', blond: 'yellow', ginger: 'orange', auburn: 'red', brunette: 'brown',
};

const RELATED: Array<[string, string]> = [
  ['red', 'orange'], ['red', 'pink'], ['orange', 'yellow'], ['green', 'teal'],
  ['teal', 'blue'], ['blue', 'purple'], ['purple', 'pink'], ['white', 'grey'], ['grey', 'black'],
];

/** Garments that are easy to confuse from a camera at speed. */
const GARMENT_FAMILY: Record<string, string> = {
  singlet: 'sleeveless', tank: 'sleeveless', 'tank top': 'sleeveless', 'crop top': 'sleeveless', 'sports bra': 'sleeveless',
  't-shirt': 'short sleeve', tee: 'short sleeve', shirt: 'short sleeve',
  'long sleeve': 'long sleeve', hoodie: 'long sleeve', jacket: 'long sleeve',
  shorts: 'shorts', 'half tights': 'shorts', skirt: 'shorts',
  tights: 'long', leggings: 'long', pants: 'long',
};

function colorFamily(c?: string | null): string | null {
  if (!c) return null;
  const key = c.trim().toLowerCase();
  return COLOR_FAMILY[key] ?? (key.split(/\s+/).map((w) => COLOR_FAMILY[w]).find(Boolean) ?? null);
}

function colorScore(a?: string | null, b?: string | null): number | null {
  const fa = colorFamily(a);
  const fb = colorFamily(b);
  if (!fa || !fb) return null;
  if (fa === fb) return 1;
  return RELATED.some(([x, y]) => (x === fa && y === fb) || (x === fb && y === fa)) ? 0.5 : 0;
}

function garmentScore(a?: string | null, b?: string | null): number | null {
  if (!a || !b) return null;
  const ka = a.trim().toLowerCase();
  const kb = b.trim().toLowerCase();
  if (ka === kb) return 1;
  const fa = GARMENT_FAMILY[ka];
  const fb = GARMENT_FAMILY[kb];
  return fa && fb && fa === fb ? 0.75 : 0;
}

/** Hair styles that look alike from a passing camera. */
const HAIR_FAMILY: Record<string, string> = {
  ponytail: 'tied', bun: 'tied', braids: 'tied', braid: 'tied', plait: 'tied', 'pigtails': 'tied',
  short: 'short', 'very short': 'short', shaved: 'short', bald: 'short', buzz: 'short', cropped: 'short',
  long: 'long', 'long loose': 'long', loose: 'long', 'shoulder length': 'long', curly: 'long',
};

function hairScore(a?: string | null, b?: string | null): number | null {
  if (!a || !b) return null;
  const fam = (h: string) => {
    const k = h.trim().toLowerCase();
    return HAIR_FAMILY[k] ?? Object.entries(HAIR_FAMILY).find(([w]) => k.includes(w))?.[1] ?? null;
  };
  const fa = fam(a);
  const fb = fam(b);
  if (!fa || !fb) return null;
  return fa === fb ? 1 : 0;
}

const ACCESSORY_WORDS = ['sunglasses', 'glasses', 'vest', 'backpack', 'gloves', 'sleeves', 'socks', 'watch', 'headphones', 'earphones', 'belt', 'bottle', 'costume', 'tutu', 'cape', 'phone'];

/** Overlap of distinctive accessories; only when both sides list some. */
function accessoryScore(a?: string[], b?: string[]): number | null {
  const words = (xs?: string[]) => new Set((xs ?? []).flatMap((x) => ACCESSORY_WORDS.filter((w) => x.toLowerCase().includes(w))));
  const wa = words(a);
  const wb = words(b);
  if (wa.size === 0 || wb.size === 0) return null;
  const shared = [...wa].filter((w) => wb.has(w)).length;
  return shared / new Set([...wa, ...wb]).size;
}

const MARK_STOPWORDS = new Set([
  'the', 'and', 'with', 'on', 'of', 'small', 'large', 'big', 'chest', 'logo', 'logos', 'text', 'print', 'side', 'front', 'back', 'left', 'right', 'sleeve', 'sleeves', 'top', 'shirt',
  // Every runner's bib carries the same sponsor print: never evidence of who someone is.
  'bib', 'bibs', 'sponsor', 'race', 'number', 'event',
]);

/**
 * Prints and text on clothing ("text GOETHE", "white side stripes") are the
 * most distinctive thing a camera sees. Overlap of their words, only when both
 * sides noted some.
 */
function marksScore(a?: string | null, b?: string | null): number | null {
  const words = (m?: string | null) =>
    new Set(
      (m ?? '')
        .toLowerCase()
        // Drop whole phrases about the race bib ("red GENERALI sponsor bib"): every runner has one.
        .split(/[,;]|\band\b/)
        .filter((phrase) => !/\b(bibs?|sponsor|race number)\b/.test(phrase))
        .join(' ')
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length >= 3 && !MARK_STOPWORDS.has(w)),
    );
  const wa = words(a);
  const wb = words(b);
  if (wa.size === 0 || wb.size === 0) return null;
  const shared = [...wa].filter((w) => wb.has(w)).length;
  return shared / Math.min(wa.size, wb.size);
}

function headwearScore(a: Outfit, b: Outfit): number | null {
  const ha = a.headwear?.trim().toLowerCase();
  const hb = b.headwear?.trim().toLowerCase();
  if (!ha || !hb) return null;
  const hasA = ha !== 'none';
  const hasB = hb !== 'none';
  if (hasA !== hasB) return 0;
  if (!hasA) return 1;
  const c = colorScore(a.headwearColor, b.headwearColor);
  const kind = ha === hb ? 1 : 0.5;
  return c === null ? kind : (kind + c) / 2;
}

const WEIGHTS = { topColor: 0.35, top: 0.15, bottomColor: 0.2, bottom: 0.1, headwear: 0.2, shoesColor: 0.1, hairColor: 0.1, hair: 0.08, accessories: 0.12, marks: 0.25 } as const;
const MIN_EVIDENCE = 0.3;

/**
 * 0..1 similarity between two outfits, or null when they share too few
 * comparable attributes to say anything.
 */
export function outfitSimilarity(a: Outfit, b: Outfit): number | null {
  const parts: Array<[number, number | null]> = [
    [WEIGHTS.topColor, colorScore(a.topColor, b.topColor)],
    [WEIGHTS.top, garmentScore(a.top, b.top)],
    [WEIGHTS.bottomColor, colorScore(a.bottomColor, b.bottomColor)],
    [WEIGHTS.bottom, garmentScore(a.bottom, b.bottom)],
    [WEIGHTS.headwear, headwearScore(a, b)],
    [WEIGHTS.shoesColor, colorScore(a.shoesColor, b.shoesColor)],
    [WEIGHTS.hairColor, colorScore(a.hairColor, b.hairColor)],
    [WEIGHTS.hair, hairScore(a.hair, b.hair)],
    [WEIGHTS.accessories, accessoryScore(a.other, b.other)],
    [WEIGHTS.marks, marksScore(a.marks, b.marks)],
  ];
  let weight = 0;
  let total = 0;
  for (const [w, s] of parts) {
    if (s === null) continue;
    weight += w;
    total += w * s;
  }
  return weight < MIN_EVIDENCE ? null : total / weight;
}

/**
 * Best match between what a camera saw and everything known about a runner:
 * each earlier sighting (people take off jackets and caps, so any one of them
 * may be the right comparison) and the roster description. Camera sightings
 * need fewer shared attributes to count, and beat the roster when both exist.
 */
export function appearanceMatch(seen: Outfit, profile: { sightings: Outfit[]; roster: Outfit | null }): { score: number; source: 'sighting' | 'roster' } | null {
  let best: { score: number; source: 'sighting' | 'roster' } | null = null;
  for (const s of profile.sightings) {
    const v = outfitSimilarity(s, seen);
    if (v !== null && (!best || v > best.score)) best = { score: v, source: 'sighting' };
  }
  if (best) return best;
  const r = profile.roster ? outfitSimilarity(profile.roster, seen) : null;
  return r === null ? null : { score: r, source: 'roster' };
}

/** Short human description, e.g. "blue singlet, black shorts, white cap, ponytail". */
export function describeOutfit(o: Outfit | null | undefined): string {
  if (!o) return '';
  const two = (c?: string | null, k?: string | null) => [c, k].filter(Boolean).join(' ');
  const parts = [
    two(o.topColor, o.top),
    o.marks ? `(${o.marks})` : '',
    two(o.bottomColor, o.bottom),
    o.headwear && o.headwear !== 'none' ? two(o.headwearColor, o.headwear) : '',
    o.shoesColor ? `${o.shoesColor} shoes` : '',
    two(o.hairColor ? `${o.hairColor} hair` : null, o.hair),
    ...(o.other ?? []),
  ];
  return parts.filter(Boolean).join(', ').replace(/, \(/g, ' (');
}
